import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from 'reactstrap';

import api from '../utils/Api';
import { trackEvent } from '../utils/Analytics';
import { authContext } from '../contexts/AuthContext';

import './PlayerCodeEntry.scss';

interface IProps {
  /** Shown above the box. Left out where the surrounding copy already says it. */
  hint?: string;
}

/**
 * The player code box, and the patron's way in. A player's code is printed on the screen
 * in the venue, so someone who has never been here before types it and lands on the song
 * screen for that player, the same as scanning it into the phone app would.
 *
 * It lives on the home screen's "Add Songs to a Player" block and on the two dead ends a
 * wrong address lands on. Wherever somebody has mistyped a code the fix is to type it
 * again, so the box goes with the message rather than sending them back to the home
 * screen to find it.
 */
const PlayerCodeEntry = ({ hint }: IProps) => {
  const context = useContext(authContext);
  const navigate = useNavigate();

  const [value, setValue] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string>(null);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    // Codes are generated lowercase but they are read off a screen that shows them in
    // capitals, so whichever way they are typed has to work.
    const code = value.trim().toLowerCase();
    if (!code) {
      return;
    }

    const token = context.auth.token;
    // Signed out there is nobody to ask, so hand the code straight to the jukebox screen
    // and let the usual sign-in redirect happen there, rather than failing here with a 401
    // the person can do nothing about.
    if (!token) {
      navigate(`/jukebox/${code}`);
      return;
    }

    setChecking(true);
    setError(null);
    try {
      const player = await api.players(`identifier/${code}`, 'GET', token, null);
      // Marks this account as connected to the player, which is what the phone app does on
      // the same step. It is not needed to add songs, so a failure here is logged rather
      // than turned into a dead end.
      await api
        .players('connect', 'POST', token, { playerIdentifier: code })
        .catch(err => console.log('Could not record the connection: ' + err.message));
      context.setSelectedPlayer({ id: player.id, name: player.name, playerIdentifier: player.playerIdentifier });
      trackEvent('jukebox_joined', { player_code: player.playerIdentifier, method: 'code' });
      navigate(`/jukebox/${player.playerIdentifier}`);
    } catch (err) {
      if (err.status === 404) trackEvent('jukebox_code_not_found');
      setError(err.status === 404 ? 'We could not find a jukebox with that code.' : err.message);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="player-code-entry">
      {!!hint && <p className="player-code-entry-hint">{hint}</p>}
      <form className="player-code-entry-form" onSubmit={handleSubmit}>
        <input
          className="player-code-entry-input"
          type="text"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="Jukebox code"
          aria-label="Jukebox code"
          maxLength={8}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
        />
        <Button color="danger" outline type="submit" style={{ width: 'auto' }} disabled={checking || !value.trim()}>
          {checking ? 'Checking' : 'Go'}
        </Button>
      </form>
      {!!error && <p className="player-code-entry-error">{error}</p>}
    </div>
  );
};

export default PlayerCodeEntry;
